import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { getCredits } from "../redux/actions/payment";
import { fetchUserDetails } from "../services/settings";
import {
  fetchProductDetails,
  fetchSalesData,
  searchProducts,
} from "../services/product";
import { fetchSettings } from "../services/settings";
import Overview from "../components/Product/Overview";
import { useDispatch, useSelector } from "react-redux";
import BasicInfo from "../components/Product/BasicInfo";
import RanksPrices from "../components/Product/RanksPrices";
import Alerts from "../components/Product/Alerts";
import ProfitCalculator from "../components/Product/ProfitCalculator";
import Chart from "../components/Product/Chart";
import Variations from "../components/Product/Variations";
import Dimension from "../components/Product/Dimensions";
import Offers from "../components/Product/Offers";
import SellerCentral from "../components/Product/SellerCentral";
import Lookup from "../components/Product/Lookup";
import ROI from "../components/Product/ROI";
import BuyBoxAnalysis from "../components/Product/BuyboxAnalysis";
import GoogleSheets from "../components/Product/Sheet/GoogleSheet";
import SearchProductCard from "../components/Product/SearchProductCard";
import WalmartSearchCard from "../components/Common/WalmartSearchCard";
import {
  FaSearch,
  FaChartLine,
  FaCreditCard,
  FaChartBar,
  FaRegBell,
  FaRegMoneyBillAlt,
  FaCalculator,
} from "react-icons/fa";
import { HiOutlineChartBar } from "react-icons/hi";
import SkeletonLine from "../components/Common/SkeletonLine";
import { getCurrency } from "../utils/helper";
import AnalysisCard from "../components/Common/AnalysisCard";
import instance from "../redux/actions/AxiosInstance/authenticated";

const ProductDetail = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [spreadsheetId, setSpreadsheetId] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [settingsData, setSettingsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);
  const [showNoResults, setShowNoResults] = useState(false);
  const [visibleCount, setVisibleCount] = useState(4);
  const [activePlatform, setActivePlatform] = useState("amazon");
  const [walmartSearchTerm, setWalmartSearchTerm] = useState("");
  const [walmartResults, setWalmartResults] = useState([]);
  const [walmartLoading, setWalmartLoading] = useState(false);
  const [walmartVisibleCount, setWalmartVisibleCount] = useState(5);
  const [walmartShowNoResults, setWalmartShowNoResults] = useState(false);
  const [asin, setAsin] = useState(searchParams.get("asin"));
  const prices = useSelector((state) => state.globalStates.prices);
  const [localPrices, setLocalPrices] = useState({
    cost_price: 0.0,
    sale_price: 0,
  });
  const runAnalysis = useSelector((state) => state.globalStates.runAnalysis);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 4);
  };

  const handleWalmartSearch = async (e) => {
    e.preventDefault();
    if (!walmartSearchTerm.trim()) return;

    try {
      setWalmartLoading(true);
      setWalmartShowNoResults(false);
      setWalmartResults([]);

      const response = await instance.post("/walmart/search", {
        query: walmartSearchTerm,
        page: 1,
        addScan: "true",
      });

      const results = response?.data?.data || [];
      setWalmartResults(results);
      setWalmartVisibleCount(5);

      if (results.length === 0) {
        setWalmartShowNoResults(true);
      }
    } catch (error) {
      console.error("Error searching Walmart products:", error);
      setWalmartShowNoResults(true);
    } finally {
      setWalmartLoading(false);
    }
  };

  const handleWalmartLoadMore = () => {
    setWalmartVisibleCount((prev) => prev + 5);
  };

  const handleSelectWalmartProduct = (product) => {
    if (product?.product_id) {
      navigate(`/walmart-product?product_id=${product.product_id}`);
    }
  };

  const handleBack = () => {
    navigate("/product");
    setAsin(null);
  };

  const fetchData = async () => {
    setLoading(true);
    const [
      creditsResponse,
      productDetails,
      userDetails,
      salesData,
      settingsData,
    ] = await Promise.all([
      getCredits(),
      fetchProductDetails(asin),
      fetchUserDetails(),
      fetchSalesData(asin),
      fetchSettings(),
    ]);

    const globalData = {
      total_fees: Number(productDetails?.profitCalculator?.total_fees?.total),
      prices: {
        cost_price: Number(
          productDetails?.profitCalculator?.prices?.cost_price
        ),
        sale_price: Number(
          productDetails?.profitCalculator?.prices?.sale_price
        ),
      },
      profit: productDetails?.profitCalculator?.profit,
      roi: productDetails?.profitCalculator?.roi,
      profit_percentage: productDetails?.profitCalculator?.profit_percentage,
      roi_percentage: productDetails?.profitCalculator?.roi_percentage,
      selectedFulfillment: "FBA",
      fullfillmentFees: {
        FBA: Number(
          productDetails?.profitCalculator?.total_fees?.fba_breakdown?.FBAFees
        ),
        FBM: Number(
          productDetails?.profitCalculator?.total_fees?.fbm_breakdown?.FBMFees
        ),
      },
    };

    dispatch({
      type: "UPDATE_GLOBAL_STATES",
      payload: globalData,
    });

    if (userDetails) {
      setSpreadsheetId(userDetails?.user?.spreadsheetId);
    }
    if (productDetails) {
      setProductDetails(productDetails);
    }
    if (salesData) {
      setSalesData(salesData);
    }
    if (settingsData) {
      setSettingsData(settingsData);
    }

    // Sync local prices with Redux prices after fetching
    setLocalPrices({
      cost_price: prices.cost_price || 0,
      sale_price: prices.sale_price || 0,
    });

    setLoading(false);
  };

  const handleLocalPriceChange = (e, type) => {
    let value = e.target.value.replace(/[^0-9.]/g, "");

    if (type === "cost_price" || type === "sale_price") {
      if (value.length > 2) {
        const dollars = value.slice(0, -2);
        const cents = value.slice(-2);
        value = `${dollars}.${cents}`;
      }
    }

    setLocalPrices({
      ...localPrices,
      [type]: value,
    });
  };

  const handleRunAnalysis = () => {
    dispatch({
      type: "SET_PRICES",
      payload: {
        ...prices,
        cost_price: localPrices.cost_price,
        sale_price: localPrices.sale_price,
      },
    });

    dispatch({
      type: "SET_RUN_ANALYSIS",
      payload: true,
    });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchTerm === "") return;
    setData([]);
    setShowNoResults(false);
    setLoading(true);
    const data = await searchProducts(searchTerm);
    setData(data);
    setLoading(false);
  };

  useEffect(() => {
    if (asin) {
      fetchData();
    }
    return () => {
      dispatch({
        type: "SET_RUN_ANALYSIS",
        payload: false,
      });
    }
  }, [asin]);

  // Sync local prices with Redux prices when they change
  useEffect(() => {
    setLocalPrices({
      cost_price: prices.cost_price || "",
      sale_price: prices.sale_price || "",
    });
  }, [prices.cost_price, prices.sale_price]);

  if (!asin)
    return (
      <div className="bg-primary-bg min-h-screen">
        <div className="title border-b-2 border-b-border w-full md:p-5 p-3 text-gray-100 flex items-center gap-4">
          <p className="text-white font-bold">Search Product</p>
        </div>
        <div className="px-2 mt-3">
          {/* Platform toggle */}
          <div className="flex justify-center mb-4">
            <div className="inline-flex rounded-lg border border-primary/40 overflow-hidden">
              <button
                className={`px-4 py-2 text-xs font-semibold border-r border-primary/40 ${
                  activePlatform === "amazon"
                    ? "bg-primary text-primary-dark"
                    : "bg-primary-bg text-primary hover:bg-primary/10"
                }`}
                onClick={() => setActivePlatform("amazon")}
                type="button"
              >
                Amazon
              </button>
              <button
                className={`px-4 py-2 text-xs font-semibold ${
                  activePlatform === "walmart"
                    ? "bg-primary text-primary-dark"
                    : "bg-primary-bg text-primary hover:bg-primary/10"
                }`}
                onClick={() => setActivePlatform("walmart")}
                type="button"
              >
                Walmart
              </button>
            </div>
          </div>

          {activePlatform === "amazon" ? (
            <>
              <form onSubmit={handleSearch} className="max-w-md mx-auto flex">
                <input
                  type="text"
                  placeholder="Search your Product"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-grow px-4 py-2 mr-2 text-white rounded-l-md border bg-primary-bg border-gray-500 focus:outline-none focus:border-primary/50"
                />
                <button
                  type="submit"
                  className="bg-primary-bg border border-primary/30 rounded-md text-primary py-2 px-3 rounded-r-md hover:bg-primary hover:text-secondary transition duration-300"
                >
                  <FaSearch />
                </button>
              </form>

              {loading && (
                <div className="mt-4">
                  <div className="text-center text-primary mt-6">
                    <div className="loading-spinner"></div>
                    <p>Loading...</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4">
                {data?.slice(0, visibleCount).map((product, index) => (
                  <SearchProductCard
                    key={index}
                    data={product}
                    loading={loading}
                    setAsin={setAsin}
                    settingsData={settingsData}
                  />
                ))}
              </div>

              {visibleCount < data?.length && (
                <div className="flex justify-center mt-2">
                  <button
                    onClick={handleLoadMore}
                    className="w-fit bg-primary/10 text-primary border border-primary text-sm font-semibold py-2 px-12 rounded-md mt-2 hover:bg-primary hover:text-secondary transition duration-300"
                  >
                    Load More
                  </button>
                </div>
              )}
              {showNoResults && (
                <div className="mt-4 text-center">
                  <p className="text-primary">No Products Found</p>
                </div>
              )}
            </>
          ) : (
            <>
              <form
                onSubmit={handleWalmartSearch}
                className="max-w-md mx-auto flex"
              >
                <input
                  type="text"
                  placeholder="Search Walmart Product"
                  value={walmartSearchTerm}
                  onChange={(e) => setWalmartSearchTerm(e.target.value)}
                  className="flex-grow px-4 py-2 mr-2 text-white rounded-l-md border bg-primary-bg border-gray-500 focus:outline-none focus:border-primary/50"
                />
                <button
                  type="submit"
                  className="bg-primary-bg border border-primary/30 rounded-md text-primary py-2 px-3 rounded-r-md hover:bg-primary hover:text-secondary transition duration-300"
                >
                  <FaSearch />
                </button>
              </form>

              {walmartLoading && (
                <div className="mt-4">
                  <div className="text-center text-primary mt-6">
                    <div className="loading-spinner"></div>
                    <p>Loading Walmart products...</p>
                  </div>
                </div>
              )}

              <div className="w-full md:w-1/2 mx-auto">
                {walmartResults
                  ?.slice(0, walmartVisibleCount)
                  .map((product, index) => (
                    <WalmartSearchCard
                      key={product.product_id || index}
                      product={product}
                      loading={walmartLoading}
                      onSelect={handleSelectWalmartProduct}
                    />
                  ))}
              </div>

              {walmartVisibleCount < walmartResults.length && (
                <div className="flex justify-center mt-2">
                  <button
                    onClick={handleWalmartLoadMore}
                    className="w-fit bg-primary/10 text-primary border border-primary text-sm font-semibold py-2 px-12 rounded-md mt-2 hover:bg-primary hover:text-secondary transition duration-300"
                  >
                    Load More
                  </button>
                </div>
              )}
              {walmartShowNoResults && (
                <div className="mt-4 text-center">
                  <p className="text-primary">No Products Found</p>
                </div>
              )}
            </>
          )}

          <div className="flex justify-center" style={{ height: "60px" }}></div>
        </div>
      </div>
    );

  return (
    <div className="bg-primary-bg min-h-screen">
      <div className="title border-b-2 border-b-border w-full md:p-4 p-3 text-gray-100 flex items-center gap-4">
        <button
          onClick={handleBack}
          className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-secondary-bg transition-colors duration-200 cursor-pointer bg-secondary-bg"
          aria-label="Back to Dashboard"
        >
          <IoArrowBack className="md:text-2xl text-xl text-gray-100 text-primary" />
        </button>
        <p className="text-white font-bold">Product Detail</p>
      </div>

      <div className="p-5">
        <Overview
          asin={asin}
          eanList={productDetails?.eanList}
          data={{ ...productDetails?.overview, image: productDetails?.image }}
          rating={productDetails?.rating}
          basicInfo={productDetails?.basicInfo}
          amazonFees={Number(
            productDetails?.profitCalculator?.total_fees?.total
          )}
          salesData={salesData}
          loading={loading}
          settingsData={settingsData}
          dimensions={productDetails?.dimensions}
          amazonLink={productDetails?.sellerCentral?.amazonLink}
          description={productDetails?.description}
          referralFeePercent={Number(
            productDetails?.profitCalculator?.total_fees?.fba_breakdown
              ?.referralFeePercent
          )}
          alerts={productDetails?.alerts}
          scores={{
            ...productDetails?.scanScore,
            salesScore: salesData?.sales_estimation?.score,
          }}
        />

        <div className="bg-primary-bg rounded-2xl py-4 sm:px-8 px-2 border-top-fade">
          <BasicInfo
            data={productDetails?.overview}
            loading={loading}
            amazonFees={Number(
              productDetails?.profitCalculator?.total_fees?.total
            )}
            salesData={salesData}
            settingsData={settingsData}
            referralFeePercent={Number(
              productDetails?.profitCalculator?.total_fees?.fba_breakdown
                ?.referralFeePercent
            )}
          />
          <hr className="my-8 border border-secondary-bg" />

          {!runAnalysis ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6 px-2">
              <AnalysisCard
                title="Chart"
                icon={FaChartLine}
                bgGradient="bg-gradient-to-br from-teal-900/40 to-teal-800/20"
                description="to generate ranks and prices"
              />

              <AnalysisCard
                title="Alerts"
                icon={FaRegBell}
                bgGradient="bg-gradient-to-br from-yellow-900/40 to-yellow-800/20"
                description="to generate alerts"
              />

              <AnalysisCard
                title="Profit calculator"
                icon={FaRegMoneyBillAlt}
                bgGradient="bg-gradient-to-br from-gray-900/40 to-gray-800/20"
                description="to show profit calculator"
              />

              <AnalysisCard
                title="Offers"
                icon={FaCreditCard}
                bgGradient="bg-gradient-to-br from-purple-900/20 to-purple-800/10"
                description="to show offers"
              />

              <AnalysisCard
                title="ROI"
                icon={FaCalculator}
                bgGradient="bg-gradient-to-br from-green-900/40 to-green-800/20"
                description="to show ROI calculations"
              />

              <AnalysisCard
                title="Buy box analysis"
                icon={HiOutlineChartBar}
                bgGradient="bg-gradient-to-br from-teal-900/40 to-teal-800/20"
                description="to show the analysis"
              />
            </div>
          ) : (
            <>
              <RanksPrices
                data={productDetails?.ranksandprices}
                loading={loading}
                salesData={salesData}
                settingsData={settingsData}
              />
              <hr className="my-8 border border-secondary-bg" />
              <Alerts data={productDetails?.alerts} />
              <hr className="my-8 border border-secondary-bg" />
              <ProfitCalculator
                data={productDetails?.profitCalculator}
                loading={loading}
                asin={asin}
                settingsData={settingsData}
              />
              <hr className="my-8 border border-secondary-bg" />
              <Chart
                data={{ asin: asin }}
                ranksandprices={productDetails?.ranksandprices}
                loading={loading}
                settingsData={settingsData}
              />
              <hr className="my-8 border border-secondary-bg" />
              <Variations loading={loading} data={productDetails?.variations} />
              <hr className="my-8 border border-secondary-bg" />
              <Dimension data={productDetails?.dimensions} loading={loading} />
              <hr className="my-8 border border-secondary-bg" />
              <SellerCentral
                asin={asin}
                loading={loading}
                settingsData={settingsData}
              />
              <hr className="my-8 border border-secondary-bg" />
              <Offers
                data={productDetails?.offers}
                loading={loading}
                settingsData={settingsData}
              />
              <hr className="my-8 border border-secondary-bg" />
              <Lookup loading={loading} asin={asin} />
              <hr className="my-8 border border-secondary-bg" />
              <ROI
                loading={loading}
                salesData={salesData}
                data={productDetails?.profitCalculator}
                settingsData={settingsData}
              />
              <hr className="my-8 border border-secondary-bg" />
              <BuyBoxAnalysis
                loading={loading}
                data={productDetails?.buyboxAnalysis}
                settingsData={settingsData}
              />
              <hr className="my-8 border border-secondary-bg" />
              <GoogleSheets
                data={productDetails?.googleSheets}
                salesData={salesData}
                spreadsheetId={spreadsheetId}
                setSpreadsheetId={setSpreadsheetId}
              />
            </>
          )}
        </div>
      </div>

      <div className="sticky bottom-[-1px] left-0 right-0 z-1 bg-[#1c211d] border-t-2 border-border rounded-t-2xl px-4 py-2">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex gap-2 md:gap-4">
            <div className="flex rounded-lg items-center py-2 justify-between bg-secondary-bg px-4">
              <div className="text-xs text-white mr-2">Cost Price</div>
              <div className="text-primary flex justify-end">
                {loading ? (
                  <div className="mb-2 px-2 w-24">
                    <SkeletonLine />
                  </div>
                ) : (
                  <input
                    type="text"
                    value={`${getCurrency(settingsData?.marketplace)}${
                      localPrices.cost_price
                    }`}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, "");
                      e.target.value = `${getCurrency(
                        settingsData?.marketplace
                      )}${rawValue}`;
                      handleLocalPriceChange(e, "cost_price");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === ".") {
                        e.preventDefault();
                      }
                    }}
                    className="text-center text-sm text-white bg-primary-bg border border-border rounded-md w-24 py-2"
                  />
                )}
              </div>
            </div>

            <div className="flex rounded-lg items-center py-2 justify-between bg-secondary-bg px-4">
              <div className="text-xs text-white mr-2">Sales Price</div>
              <div className="text-primary flex justify-end">
                {loading ? (
                  <div className="mb-2 px-2 w-24">
                    <SkeletonLine />
                  </div>
                ) : (
                  <input
                    type="text"
                    value={`${getCurrency(settingsData?.marketplace)}${
                      localPrices.sale_price
                    }`}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, "");
                      e.target.value = `${getCurrency(
                        settingsData?.marketplace
                      )}${rawValue}`;
                      handleLocalPriceChange(e, "sale_price");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === ".") {
                        e.preventDefault();
                      }
                    }}
                    className="text-center text-sm text-white bg-primary-bg border border-border rounded-md w-24 py-2"
                  />
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleRunAnalysis}
            disabled={loading}
            className="bg-primary text-primary-bg font-semibold py-2 px-28 rounded-lg hover:bg-primary/70 disabled:opacity-50 disabled:cursor-not-allowed transition duration-300 whitespace-nowrap"
          >
            Run Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
